// Phase JB.1 — Behavioural event tracking client.
//
// Single typed export `trackEvent(name, payload)`. The 13 event names
// from Free-to-Paid Conversion Architecture v1.0 §4 are a closed
// TypeScript union — no string literals from callers. Anon-friendly:
// when there is no auth session, user_id is left null.
//
// Doctrine:
//   - N01 no fabrication: we record only what the caller passes; the
//     payload is opaque jsonb, never enriched with invented signals.
//   - Buffered: events flush every 5s or on page hide to avoid
//     row-per-event chatter. The buffer is a plain in-memory array;
//     no localStorage/sessionStorage.
//
// Failure mode: insertion errors are swallowed — analytics must never
// break a render path. They are surfaced through `console.warn` only.

import { getBrowserClient } from "../supabase/browser.ts";

export type ConversionEventName =
  | "signal_viewed"
  | "signal_clicked"
  | "signal_filtered"
  | "slip_leg_added"
  | "slip_leg_removed"
  | "slip_locked"
  | "gap_report_viewed"
  | "paywall_seen"
  | "pricing_viewed"
  | "trial_started"
  | "trial_expired"
  | "referral_link_copied"
  | "pro_signal_glimpsed"
  | "swarm_slip_uploaded"
  | "swarm_extraction_complete"
  | "swarm_analysis_complete"
  | "swarm_leg_dropped"
  | "btb_board_viewed"
  | "btb_selection_expanded";

export const ALL_EVENT_NAMES: readonly ConversionEventName[] = [
  "signal_viewed",
  "signal_clicked",
  "signal_filtered",
  "slip_leg_added",
  "slip_leg_removed",
  "slip_locked",
  "gap_report_viewed",
  "paywall_seen",
  "pricing_viewed",
  "trial_started",
  "trial_expired",
  "referral_link_copied",
  "pro_signal_glimpsed",
  "swarm_slip_uploaded",
  "swarm_extraction_complete",
  "swarm_analysis_complete",
  "swarm_leg_dropped",
  "btb_board_viewed",
  "btb_selection_expanded",
] as const;

export interface QueuedEvent {
  event_name: ConversionEventName;
  event_payload: Record<string, unknown>;
  occurred_at: string;
  session_id: string;
  user_agent: string;
}

const FLUSH_INTERVAL_MS = 5000;

function newSessionId(): string {
  // Per-tab session identity. Survives in-memory only (no storage).
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

interface BufferState {
  buffer: QueuedEvent[];
  sessionId: string;
  timer: ReturnType<typeof setTimeout> | null;
  unloadBound: boolean;
}

const state: BufferState = {
  buffer: [],
  sessionId: newSessionId(),
  timer: null,
  unloadBound: false,
};

function bindUnload(): void {
  if (state.unloadBound || typeof window === "undefined") return;
  state.unloadBound = true;
  window.addEventListener("pagehide", () => {
    void flushEvents();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushEvents();
  });
}

function scheduleFlush(): void {
  if (state.timer !== null) return;
  state.timer = setTimeout(() => {
    state.timer = null;
    void flushEvents();
  }, FLUSH_INTERVAL_MS);
}

export async function flushEvents(): Promise<void> {
  if (state.buffer.length === 0) return;
  const batch = state.buffer.splice(0, state.buffer.length);
  try {
    const sb = getBrowserClient();
    const { data: sess } = await sb.auth.getSession();
    const uid = sess.session?.user.id ?? null;
    const rows = batch.map((e) => ({
      user_id: uid,
      session_id: e.session_id,
      event_name: e.event_name,
      event_payload: e.event_payload,
      occurred_at: e.occurred_at,
      user_agent: e.user_agent,
    }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (sb.from("conversion_events" as never) as any).insert(rows);
    if (error) {
      console.warn("[trackEvent] insert failed:", error.message);
    }
  } catch (e) {
    console.warn("[trackEvent] flush failed:", (e as Error).message);
  }
}

export function trackEvent(
  name: ConversionEventName,
  payload: Record<string, unknown> = {},
): void {
  if (!ALL_EVENT_NAMES.includes(name)) {
    console.warn("[trackEvent] rejected unknown event name:", name);
    return;
  }
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  state.buffer.push({
    event_name: name,
    event_payload: payload,
    occurred_at: new Date().toISOString(),
    session_id: state.sessionId,
    user_agent: ua,
  });
  bindUnload();
  scheduleFlush();
}

// Test seam: reset internal state.
export function __resetTrackEventForTests(): void {
  state.buffer.length = 0;
  if (state.timer !== null) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  state.sessionId = newSessionId();
  state.unloadBound = false;
}

// Test seam: peek queued buffer length.
export function __peekBufferForTests(): readonly QueuedEvent[] {
  return state.buffer.slice();
}
